import type { ReactNode } from 'react'
import { Info, Warning, WarningOctagon, CheckCircle } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'

export type BannerTone = 'info' | 'warn' | 'error' | 'success'

const styles: Record<BannerTone, { box: string; icon: ReactNode }> = {
  info: { box: 'border-info/30 bg-info-wash text-info', icon: <Info weight="bold" /> },
  warn: { box: 'border-warn/30 bg-warn-wash text-warn', icon: <Warning weight="bold" /> },
  error: {
    box: 'border-danger/30 bg-danger-wash text-danger',
    icon: <WarningOctagon weight="bold" />,
  },
  success: { box: 'border-ok/30 bg-ok-wash text-ok', icon: <CheckCircle weight="bold" /> },
}

export interface BannerProps {
  tone?: BannerTone
  title?: ReactNode
  children?: ReactNode
  action?: ReactNode
  className?: string
}

export function Banner({ tone = 'info', title, children, action, className }: BannerProps) {
  const s = styles[tone]
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-card border px-3 py-2.5 text-[14px]',
        s.box,
        className,
      )}
    >
      <span className="mt-0.5 shrink-0 text-[18px]" aria-hidden="true">
        {s.icon}
      </span>
      <div className="min-w-0 flex-1 text-ink">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cn(title && 'mt-0.5', 'text-ink-soft')}>{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  )
}
